/**
 * Mileage calculator (§A6g) — estimates a bid's round-trip miles from the real
 * journey. Auto-builds a per-day itinerary (base → pickup warehouse(s) → site →
 * disposal → base), routes each leg through Azure Maps (server-side), shows an
 * editable leg-by-leg breakdown beside a Leaflet journey map, and on confirm
 * returns { miles, itinerary } to persist. Each day is its own round trip, so
 * the leg sum IS the round-trip total (no doubling).
 */
import 'leaflet/dist/leaflet.css';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, MapPin, Plus, Trash2, TriangleAlert } from 'lucide-react';
import type {
  LegCategory,
  MileageItinerary,
  MileageLeg,
  MileagePoint,
  RouteLegResult,
} from '@vari-crew/schemas';
import { itineraryTotalMiles } from '@vari-crew/schemas';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { AddressAutocomplete } from '@/components/ui/AddressAutocomplete';
import { useOriginLocations, type OriginLocation } from '@/api/locations';
import { useRouteLegs } from '@/api/route';
import { formatMoneyCents } from '@/lib/format';
import { cn } from '@/lib/cn';

/** Context the bid parents (create form / project detail) hand the calculator. */
export interface RoutingContext {
  siteLat: number | null;
  siteLng: number | null;
  siteLabel: string | null;
  originWarehouses: string[];
  dayCount: number;
  hasDebris: boolean;
}

const DISPOSAL_DEFAULT_NAME = 'VariSpace Southlake';

type StopRole = 'base' | 'pickup' | 'site' | 'disposal' | 'custom';
interface Stop {
  key: string;
  role: StopRole;
  point: MileagePoint | null; // null = unresolved (needs a location)
}
interface DayPlan {
  stops: Stop[];
}

const CAT_LABEL: Record<LegCategory, string> = {
  depart: 'Depart',
  pickup: 'Pickup',
  to_site: 'To site',
  disposal: 'Disposal',
  return: 'Return',
  other: 'Travel',
};
const CAT_COLOR: Record<LegCategory, string> = {
  depart: '#64748b',
  pickup: '#d97706',
  to_site: '#2563eb',
  disposal: '#7c3aed',
  return: '#475569',
  other: '#0891b2',
};

const uid = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `s${Math.random()}`;

const sameCoord = (a: MileagePoint | null, b: { lat: number; lng: number } | null) =>
  !!a && !!b && Math.abs(a.lat - b.lat) < 5e-4 && Math.abs(a.lng - b.lng) < 5e-4;

function categorize(from: Stop, to: Stop): LegCategory {
  if (from.role === 'base') return 'depart';
  if (to.role === 'base') return 'return';
  if (to.role === 'site') return 'to_site';
  if (to.role === 'disposal') return 'disposal';
  if (to.role === 'pickup') return 'pickup';
  return 'other';
}

function findWarehouse(name: string, known: OriginLocation[]): OriginLocation | undefined {
  const n = name.trim().toLowerCase();
  return known.find((k) => k.name.trim().toLowerCase() === n);
}

/** Display label for a known origin — name + street so the map/legs aren't a bare
 *  name like "South Lake" (demo #5). Falls back to just the name. */
function originLabel(l: { name: string; line1?: string | null; city?: string | null }): string {
  const addr = [l.line1, l.city].filter(Boolean).join(', ');
  return addr ? `${l.name} — ${addr}` : l.name;
}

/** Build the default per-day itinerary from the project context. base may be null. */
function buildDefaultDays(
  routing: RoutingContext,
  known: OriginLocation[],
  base: MileagePoint | null,
  includeDisposal: boolean,
): DayPlan[] {
  const baseStop = (): Stop => ({ key: uid(), role: 'base', point: base });
  const site: MileagePoint | null =
    routing.siteLat != null && routing.siteLng != null
      ? { label: routing.siteLabel || 'Job site', lat: routing.siteLat, lng: routing.siteLng }
      : null;
  const pickups: Stop[] = [...new Set(routing.originWarehouses.map((w) => w.trim()).filter(Boolean))].map(
    (name) => {
      const loc = findWarehouse(name, known);
      return {
        key: uid(),
        role: 'pickup' as StopRole,
        point: loc ? { label: originLabel(loc), lat: loc.latitude, lng: loc.longitude, location_id: loc.id } : null,
      };
    },
  );
  const disposalLoc = findWarehouse(DISPOSAL_DEFAULT_NAME, known);
  const disposalStop = (): Stop => ({
    key: uid(),
    role: 'disposal',
    point: disposalLoc
      ? { label: originLabel(disposalLoc), lat: disposalLoc.latitude, lng: disposalLoc.longitude, location_id: disposalLoc.id }
      : { label: DISPOSAL_DEFAULT_NAME, lat: 0, lng: 0 },
  });
  const siteStop = (): Stop => ({ key: uid(), role: 'site', point: site });
  const days = Math.max(1, routing.dayCount);

  const out: DayPlan[] = [];
  for (let d = 1; d <= days; d++) {
    const stops: Stop[] = [baseStop()];
    if (d === 1) for (const p of pickups) stops.push({ ...p, key: uid() });
    stops.push(siteStop());
    const disposalDay = days === 1 ? d === 1 : d === days;
    if (includeDisposal && disposalDay && disposalLoc) stops.push(disposalStop());
    stops.push(baseStop());
    out.push({ stops });
  }
  return out;
}

/** Reconstruct editable days from a saved itinerary (re-entry). */
function daysFromItinerary(it: MileageItinerary, routing: RoutingContext): DayPlan[] {
  const site = routing.siteLat != null && routing.siteLng != null
    ? { lat: routing.siteLat, lng: routing.siteLng }
    : null;
  const roleFor = (p: MileagePoint): StopRole => {
    if (sameCoord(p, it.base)) return 'base';
    if (sameCoord(p, site)) return 'site';
    return p.location_id ? 'pickup' : 'custom';
  };
  const byDay = new Map<number, MileageLeg[]>();
  for (const leg of it.legs) {
    const arr = byDay.get(leg.day) ?? [];
    arr.push(leg);
    byDay.set(leg.day, arr);
  }
  const days: DayPlan[] = [];
  for (const day of [...byDay.keys()].sort((a, b) => a - b)) {
    const legs = byDay.get(day)!;
    const stops: Stop[] = [];
    stops.push({ key: uid(), role: legs[0]!.category === 'depart' ? 'base' : roleFor(legs[0]!.from), point: legs[0]!.from });
    for (const leg of legs) {
      const role: StopRole =
        leg.category === 'return' ? 'base'
        : leg.category === 'to_site' ? 'site'
        : leg.category === 'disposal' ? 'disposal'
        : leg.category === 'pickup' ? 'pickup'
        : roleFor(leg.to);
      stops.push({ key: uid(), role, point: leg.to });
    }
    days.push({ stops });
  }
  return days;
}

export function MileageCalculatorModal({
  open,
  onClose,
  routing,
  initial,
  ratePerMileCents,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  routing: RoutingContext;
  initial: MileageItinerary | null;
  ratePerMileCents: number;
  onConfirm: (miles: number, itinerary: MileageItinerary) => void;
}) {
  const { data: known } = useOriginLocations();
  const knownLocations = useMemo(() => known?.data ?? [], [known]);
  const routeLegs = useRouteLegs();

  const [base, setBase] = useState<MileagePoint | null>(initial?.base ?? null);
  const [includeDisposal, setIncludeDisposal] = useState(initial?.include_disposal ?? routing.hasDebris);
  const [days, setDays] = useState<DayPlan[]>([]);
  // Per-day routed legs (miles + geometry), keyed by day index.
  const [routed, setRouted] = useState<Map<number, RouteLegResult[]>>(new Map());
  const [estimated, setEstimated] = useState(false);
  const initializedRef = useRef(false);

  // Initialize once per open: reconstruct a saved itinerary, else auto-build.
  useEffect(() => {
    if (!open) {
      initializedRef.current = false;
      return;
    }
    if (initializedRef.current) return;
    if (!known) return; // wait for locations so warehouse names resolve
    initializedRef.current = true;
    if (initial && initial.legs.length > 0) {
      setBase(initial.base);
      setIncludeDisposal(initial.include_disposal);
      setDays(daysFromItinerary(initial, routing));
    } else {
      setDays(buildDefaultDays(routing, knownLocations, base, includeDisposal));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, known]);

  // Base flows into every base bookend.
  useEffect(() => {
    setDays((ds) =>
      ds.map((d) => ({ stops: d.stops.map((s) => (s.role === 'base' ? { ...s, point: base } : s)) })),
    );
  }, [base]);

  // Recompute routes (debounced) whenever the resolvable plan changes.
  const signature = useMemo(
    () =>
      days
        .map((d) => d.stops.map((s) => (s.point ? `${s.point.lat},${s.point.lng}` : 'x')).join('>'))
        .join('|'),
    [days],
  );
  useEffect(() => {
    if (!open) return;
    const resolved = days
      .map((d, idx) => ({ idx, pts: d.stops.map((s) => s.point) }))
      .filter((d) => d.pts.length >= 2 && d.pts.every((p): p is MileagePoint => !!p))
      .map((d) => ({ idx: d.idx, coords: d.pts.map((p) => ({ lat: p!.lat, lng: p!.lng })) }));
    if (resolved.length === 0) {
      setRouted(new Map());
      setEstimated(false);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await routeLegs.mutateAsync({ sequences: resolved.map((r) => r.coords) });
        if (cancelled) return;
        const next = new Map<number, RouteLegResult[]>();
        res.sequences.forEach((seq, i) => next.set(resolved[i]!.idx, seq.legs));
        setRouted(next);
        setEstimated(res.estimated);
      } catch {
        /* leave prior routed in place */
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, open]);

  // --- derived display legs ---
  interface DisplayLeg {
    dayIdx: number;
    i: number;
    category: LegCategory;
    from: Stop;
    to: Stop;
    miles: number;
    estimated: boolean;
    points: { lat: number; lng: number }[];
    resolved: boolean;
  }
  const displayLegs: DisplayLeg[] = useMemo(() => {
    const out: DisplayLeg[] = [];
    days.forEach((d, dayIdx) => {
      const r = routed.get(dayIdx);
      for (let i = 0; i < d.stops.length - 1; i++) {
        const from = d.stops[i]!;
        const to = d.stops[i + 1]!;
        const leg = r?.[i];
        out.push({
          dayIdx,
          i,
          category: categorize(from, to),
          from,
          to,
          miles: leg?.miles ?? 0,
          estimated: leg?.estimated ?? false,
          points: leg?.points ?? [],
          resolved: !!(from.point && to.point),
        });
      }
    });
    return out;
  }, [days, routed]);

  const unresolvedCount = displayLegs.filter((l) => !l.resolved).length;
  const baseMissing = !base;
  const totalMiles = itineraryTotalMiles(displayLegs.filter((l) => l.resolved).map((l) => ({ miles: l.miles })));
  const canConfirm = !baseMissing && unresolvedCount === 0 && displayLegs.length > 0;

  // --- editing ---
  function updateStop(dayIdx: number, stopKey: string, point: MileagePoint | null, role?: StopRole) {
    setDays((ds) =>
      ds.map((d, i) =>
        i !== dayIdx
          ? d
          : { stops: d.stops.map((s) => (s.key === stopKey ? { ...s, point, role: role ?? s.role } : s)) },
      ),
    );
  }
  function addStop(dayIdx: number) {
    setDays((ds) =>
      ds.map((d, i) => {
        if (i !== dayIdx) return d;
        const stops = [...d.stops];
        const insertAt = Math.max(1, stops.length - 1); // before the final base
        stops.splice(insertAt, 0, { key: uid(), role: 'custom', point: null });
        return { stops };
      }),
    );
  }
  function removeStop(dayIdx: number, stopKey: string) {
    setDays((ds) =>
      ds.map((d, i) => (i !== dayIdx ? d : { stops: d.stops.filter((s) => s.key !== stopKey) })),
    );
  }
  function setDayCount(n: number) {
    const next = Math.max(1, Math.min(30, n));
    setDays((ds) => {
      if (next === ds.length) return ds;
      if (next < ds.length) return ds.slice(0, next);
      const extra: DayPlan[] = [];
      const site = routing.siteLat != null && routing.siteLng != null
        ? { label: routing.siteLabel || 'Job site', lat: routing.siteLat, lng: routing.siteLng }
        : null;
      for (let d = ds.length; d < next; d++) {
        extra.push({
          stops: [
            { key: uid(), role: 'base', point: base },
            { key: uid(), role: 'site', point: site },
            { key: uid(), role: 'base', point: base },
          ],
        });
      }
      return [...ds, ...extra];
    });
  }
  function toggleDisposal(on: boolean) {
    setIncludeDisposal(on);
    setDays((ds) => {
      const disposalLoc = findWarehouse(DISPOSAL_DEFAULT_NAME, knownLocations);
      const disposalPoint: MileagePoint | null = disposalLoc
        ? { label: originLabel(disposalLoc), lat: disposalLoc.latitude, lng: disposalLoc.longitude, location_id: disposalLoc.id }
        : null;
      const lastDay = ds.length - 1;
      return ds.map((d, i) => {
        const isDisposalDay = i === lastDay;
        const stops = d.stops.filter((s) => s.role !== 'disposal');
        if (on && isDisposalDay) {
          stops.splice(Math.max(1, stops.length - 1), 0, { key: uid(), role: 'disposal', point: disposalPoint });
        }
        return { stops };
      });
    });
  }

  function confirm() {
    if (!canConfirm || !base) return;
    const legs: MileageLeg[] = displayLegs.map((l) => ({
      day: l.dayIdx + 1,
      category: l.category,
      from: l.from.point!,
      to: l.to.point!,
      miles: l.miles,
      estimated: l.estimated,
    }));
    const itinerary: MileageItinerary = {
      version: 1,
      legs,
      total_miles: totalMiles,
      base,
      include_disposal: includeDisposal,
    };
    onConfirm(totalMiles, itinerary);
  }

  // Options for the per-stop pickers: the job site + known origin locations.
  const stopOptions = useMemo(() => {
    const opts: { id: string; label: string; lat: number; lng: number; role: StopRole }[] = [];
    if (routing.siteLat != null && routing.siteLng != null)
      opts.push({ id: '__site__', label: routing.siteLabel || 'Job site', lat: routing.siteLat, lng: routing.siteLng, role: 'site' });
    for (const l of knownLocations)
      opts.push({ id: l.id, label: l.name, lat: l.latitude, lng: l.longitude, role: 'pickup' });
    return opts;
  }, [knownLocations, routing]);

  const mapLegs = displayLegs
    .filter((l) => l.points.length > 1)
    .map((l) => ({ points: l.points, color: CAT_COLOR[l.category] }));
  const mapMarkers = useMemo(() => {
    const seen = new Set<string>();
    const markers: { lat: number; lng: number; label: string; color: string }[] = [];
    for (const d of days)
      for (const s of d.stops) {
        if (!s.point) continue;
        const key = `${s.point.lat.toFixed(4)},${s.point.lng.toFixed(4)}`;
        if (seen.has(key)) continue;
        seen.add(key);
        markers.push({
          lat: s.point.lat,
          lng: s.point.lng,
          label: s.point.label,
          color: s.role === 'base' ? '#0b1f3a' : CAT_COLOR[s.role === 'site' ? 'to_site' : s.role === 'disposal' ? 'disposal' : s.role === 'pickup' ? 'pickup' : 'other'],
        });
      }
    return markers;
  }, [days]);

  return (
    <Dialog open={open} onClose={onClose} title="Estimate round-trip miles" className="max-w-7xl">
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,440px)_minmax(0,1fr)] min-h-[62vh]">
        {/* Left: controls + leg list */}
        <div className="p-4 space-y-4 overflow-auto max-h-[78vh] border-b lg:border-b-0 lg:border-r border-line">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-ink-faint">Vehicle base</label>
              <div className="mt-1">
                <MileageLocationPicker
                  value={base}
                  options={knownLocations.map((l) => ({
                    id: l.id,
                    label: originLabel(l),
                    lat: l.latitude,
                    lng: l.longitude,
                    role: 'base' as StopRole,
                  }))}
                  placeholder="Choose base…"
                  invalid={baseMissing}
                  onChange={(p) => setBase(p)}
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-ink-faint">Days on site</label>
              <input
                type="number"
                min={1}
                max={30}
                value={days.length || 1}
                onChange={(e) => setDayCount(parseInt(e.target.value, 10) || 1)}
                className="w-full h-9 mt-1 rounded-md border border-line bg-transparent px-2 text-sm"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={includeDisposal} onChange={(e) => toggleDisposal(e.target.checked)} />
            Include a debris-disposal stop
            <span className="text-[11px] text-ink-faint">(default {DISPOSAL_DEFAULT_NAME})</span>
          </label>

          {baseMissing && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <TriangleAlert className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-600" />
              Pick the vehicle base to calculate miles — each day starts and ends there.
            </div>
          )}
          {estimated && (
            <div className="rounded-md border border-line bg-fill-soft px-3 py-2 text-xs text-ink-dim">
              Showing straight-line estimates — Azure Maps routing is unavailable.
            </div>
          )}

          {days.map((day, dayIdx) => {
            const dayLegs = displayLegs.filter((l) => l.dayIdx === dayIdx);
            const daySubtotal = itineraryTotalMiles(dayLegs.filter((l) => l.resolved).map((l) => ({ miles: l.miles })));
            return (
              <div key={dayIdx} className="rounded-lg border border-line">
                <div className="flex items-center justify-between px-3 py-1.5 border-b border-line bg-fill-soft/50 text-xs font-medium">
                  <span>Day {dayIdx + 1}</span>
                  <span className="text-ink-faint tabular-nums">{daySubtotal} mi</span>
                </div>
                <div className="p-2 space-y-1">
                  {day.stops.map((stop, i) => {
                    const leg = dayLegs.find((l) => l.i === i);
                    return (
                      <div key={stop.key}>
                        <StopRow
                          stop={stop}
                          options={stopOptions}
                          onChange={(point, role) => updateStop(dayIdx, stop.key, point, role)}
                          onRemove={
                            stop.role !== 'base' ? () => removeStop(dayIdx, stop.key) : undefined
                          }
                        />
                        {leg && (
                          <div className="flex items-center gap-2 pl-3 py-1 text-[11px]">
                            <span
                              className="inline-block w-2 h-2 rounded-full shrink-0"
                              style={{ backgroundColor: CAT_COLOR[leg.category] }}
                            />
                            <span className="text-ink-faint">{CAT_LABEL[leg.category]}</span>
                            <span className="flex-1 border-b border-dashed border-line" />
                            {leg.resolved ? (
                              <span className="tabular-nums text-ink-dim">
                                {leg.miles.toFixed(1)} mi{leg.estimated ? ' *' : ''}
                              </span>
                            ) : (
                              <span className="text-amber-700">needs location</span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => addStop(dayIdx)}
                    className="mt-1 flex items-center gap-1 text-[11px] text-accent hover:underline"
                  >
                    <Plus className="w-3 h-3" /> Add stop
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Right: map + footer */}
        <div className="flex flex-col">
          <JourneyMap legs={mapLegs} markers={mapMarkers} />
          <div className="p-4 border-t border-line space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <span className="text-ink-faint">Round-trip total</span>
                <div className="text-2xl font-semibold font-mono">
                  {totalMiles} mi
                  {routeLegs.isPending && <Loader2 className="inline w-4 h-4 ml-2 animate-spin text-ink-faint" />}
                </div>
              </div>
              <div className="text-right text-xs text-ink-faint">
                ≈ {formatMoneyCents(Math.round(totalMiles * ratePerMileCents))}
                <div>at {formatMoneyCents(ratePerMileCents)}/mi</div>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={onClose}>
                Cancel
              </Button>
              <Button size="sm" onClick={confirm} disabled={!canConfirm}>
                Use these miles
              </Button>
            </div>
            {!canConfirm && !baseMissing && unresolvedCount > 0 && (
              <p className="text-[11px] text-amber-700 text-right">
                {unresolvedCount} leg{unresolvedCount > 1 ? 's' : ''} still need a location.
              </p>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * A location picker — a known-location dropdown plus a "Custom address…" option
 * that reveals address autocomplete (for edge cases — an unlisted base, an
 * ad-hoc supplier pickup, etc.). Shared by the vehicle-base control and the
 * per-stop rows.
 */
function MileageLocationPicker({
  value,
  options,
  placeholder,
  invalid,
  onChange,
}: {
  value: MileagePoint | null;
  options: { id: string; label: string; lat: number; lng: number; role: StopRole }[];
  placeholder: string;
  invalid?: boolean;
  onChange: (point: MileagePoint | null, role?: StopRole) => void;
}) {
  const matchedId =
    options.find((o) => (value?.location_id && o.id === value.location_id) || sameCoord(value, o))?.id ??
    (value ? '__custom__' : '');
  const [customOpen, setCustomOpen] = useState(matchedId === '__custom__');
  const [customText, setCustomText] = useState(value && matchedId === '__custom__' ? value.label : '');

  return (
    <div className="space-y-1 min-w-0">
      <select
        value={matchedId}
        onChange={(e) => {
          const v = e.target.value;
          if (v === '__custom__') {
            setCustomOpen(true);
            return;
          }
          setCustomOpen(false);
          const o = options.find((x) => x.id === v);
          if (o)
            onChange(
              { label: o.label, lat: o.lat, lng: o.lng, location_id: o.id === '__site__' ? null : o.id },
              o.role,
            );
          else onChange(null);
        }}
        className={cn(
          'w-full h-8 rounded-md border bg-transparent px-2 text-sm min-w-0',
          invalid ? 'border-amber-400' : 'border-line',
        )}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
        <option value="__custom__">Custom address…</option>
      </select>
      {customOpen && (
        <AddressAutocomplete
          value={customText}
          onChange={setCustomText}
          onSelect={(s) => {
            if (s.latitude != null && s.longitude != null) {
              setCustomText(s.poi_name || s.label);
              onChange({ label: s.poi_name || s.label, lat: s.latitude, lng: s.longitude }, 'custom');
            }
          }}
          placeholder="Search an address…"
          className="h-8"
        />
      )}
    </div>
  );
}

/** One editable stop — the location picker, plus a remove button (non-base). */
function StopRow({
  stop,
  options,
  onChange,
  onRemove,
}: {
  stop: Stop;
  options: { id: string; label: string; lat: number; lng: number; role: StopRole }[];
  onChange: (point: MileagePoint | null, role?: StopRole) => void;
  onRemove?: () => void;
}) {
  if (stop.role === 'base') {
    return (
      <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-fill-soft text-sm">
        <MapPin className="w-3.5 h-3.5 text-ink-faint shrink-0" />
        <span className="font-medium">Base</span>
        <span className="text-ink-faint truncate">{stop.point?.label ?? '— not set —'}</span>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-1.5">
      <div className="flex-1 min-w-0">
        <MileageLocationPicker
          value={stop.point}
          options={options}
          placeholder="Choose location…"
          onChange={onChange}
        />
      </div>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove stop"
          className="w-8 h-8 grid place-items-center rounded-md border border-line text-ink-faint hover:bg-fill-soft shrink-0"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

/** Leaflet journey map — OSM tiles, route polylines, numbered stop pins. */
function JourneyMap({
  legs,
  markers,
}: {
  legs: { points: { lat: number; lng: number }[]; color: string }[];
  markers: { lat: number; lng: number; label: string; color: string }[];
}) {
  const ref = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const layerRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const LRef = useRef<any>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const L = await import('leaflet');
      if (cancelled || !ref.current || mapRef.current) return;
      LRef.current = L;
      const map = L.map(ref.current, { scrollWheelZoom: true }).setView([32.95, -97.05], 9);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19,
      }).addTo(map);
      layerRef.current = L.layerGroup().addTo(map);
      mapRef.current = map;
      setTimeout(() => map.invalidateSize(), 100); // Dialog mounts then animates
    })();
    return () => {
      cancelled = true;
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const L = LRef.current;
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!L || !map || !layer) return;
    layer.clearLayers();
    const bounds: [number, number][] = [];
    for (const leg of legs) {
      const latlngs = leg.points.map((p) => [p.lat, p.lng] as [number, number]);
      L.polyline(latlngs, { color: leg.color, weight: 4, opacity: 0.85 }).addTo(layer);
      bounds.push(...latlngs);
    }
    markers.forEach((m, i) => {
      const icon = L.divIcon({
        className: '',
        html: `<div style="background:${m.color};color:#fff;width:22px;height:22px;border-radius:50%;display:grid;place-items:center;font-size:11px;font-weight:600;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)">${i + 1}</div>`,
        iconSize: [22, 22],
        iconAnchor: [11, 11],
      });
      L.marker([m.lat, m.lng], { icon, title: m.label }).addTo(layer);
      bounds.push([m.lat, m.lng]);
    });
    if (bounds.length > 0) map.fitBounds(bounds, { padding: [30, 30], maxZoom: 13 });
    setTimeout(() => map.invalidateSize(), 50);
  }, [legs, markers]);

  return <div ref={ref} className="w-full flex-1 min-h-[300px] lg:min-h-[420px] bg-fill-soft" />;
}
